import { useEffect, useState } from 'react';
import { crm, type LeadListRow } from '@/lib/crm-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface Props {
  liste: LeadListRow;
  onClose: () => void;
  onSaved: () => void;
}

/** Name und Notiz einer Lead-Liste bearbeiten. Die Zielgruppe steht seit dem Anlegen fest. */
export function RenameLeadListDialog({ liste, onClose, onSaved }: Props) {
  const [name, setName] = useState(liste.name);
  const [notes, setNotes] = useState(liste.notes ?? '');
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const speichern = async () => {
    if (!name.trim()) return;
    setBusy(true);
    setFehler(null);
    try {
      await crm.leadLists.update(liste.id, {
        name: name.trim(),
        notes: notes.trim() || null,
      });
      onSaved();
    } catch (err) {
      setFehler(err instanceof Error ? err.message : 'Speichern fehlgeschlagen');
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-6">
      <div className="w-full max-w-md space-y-4 rounded-lg bg-card p-6 shadow-xl">
        <h2 className="text-lg font-semibold">Liste bearbeiten</h2>

        <label className="block space-y-1 text-sm">
          <span className="font-medium">Name</span>
          <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </label>

        <label className="block space-y-1 text-sm">
          <span className="font-medium">Notiz</span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="flex w-full rounded-md border border-input bg-card px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            placeholder="Woher die Liste stammt, was damit geschehen soll …"
          />
        </label>

        {fehler && <p className="text-sm text-danger">{fehler}</p>}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button onClick={() => void speichern()} disabled={busy || !name.trim()}>
            {busy ? 'Speichere …' : 'Speichern'}
          </Button>
        </div>
      </div>
    </div>
  );
}
